import { useMemo, useState } from 'react'
import { Box, Collapse, Link, Tooltip } from '@mui/material'

import DropdownActionButtonMui from '../../../../components/action_button/DropdownActionButtonMui'
import { canManagePocStoreRelations } from '../../../../global_context/capabilities/pocCapabilities'
import StoreRelationUserChip from './StoreRelationUserChip'
import {
  STORE_RELATIONS_FALLBACK_TEXT,
  STORE_RELATIONS_SECONDARY_USERS_TOGGLE_LABELS
} from '../../constants/storeConstants'
import {
  buildStoreRelationRowActions,
  getStoreRelationUserChipKey,
  getStoreRelationUserChipLabel,
  getStoreRelationUserChipStatus,
  resolveStoreRelationRowState
} from '../../helpers/storeRelationRowUtils'

const StoreRelationsRow = ({
  model,
  onShowDetails,
  onAssignPrincipal,
  onReassignPrincipal,
  onSuspend,
  onEdit,
  onReactivate
}) => {
  const [showAllSecondaryUsers, setShowAllSecondaryUsers] = useState(false)
  const canWrite = canManagePocStoreRelations()
  const rowState = useMemo(() => resolveStoreRelationRowState(model), [model])
  const actions = useMemo(() => {
    if (!canWrite) return []

    return buildStoreRelationRowActions({
      model,
      canAssignPrincipal: rowState.canAssignPrincipal,
      canReassignPrincipal: rowState.canReassignPrincipal,
      canEditRelation: rowState.canEditRelation,
      canSuspendRelation: rowState.canSuspendRelation,
      canReactivateRelation: rowState.canReactivateRelation,
      onAssignPrincipal,
      onReassignPrincipal,
      onEdit,
      onSuspend,
      onReactivate
    })
  }, [
    canWrite,
    model,
    onAssignPrincipal,
    onEdit,
    onReactivate,
    onReassignPrincipal,
    onSuspend,
    rowState
  ])

  return (
    <tr className="cursor-pointer" onClick={() => onShowDetails?.(model)}>
      <td className="oc-td-text">
        {rowState.supplierLabel !== STORE_RELATIONS_FALLBACK_TEXT ? (
          <Tooltip title={rowState.supplierLabel} arrow placement="top">
            <Box component="span">{rowState.supplierLabel}</Box>
          </Tooltip>
        ) : (
          STORE_RELATIONS_FALLBACK_TEXT
        )}
      </td>

      <td className="oc-td-text">
        {rowState.buyerLabel !== STORE_RELATIONS_FALLBACK_TEXT ? (
          <Tooltip title={rowState.buyerLabel} arrow placement="top">
            <Box component="span">{rowState.buyerLabel}</Box>
          </Tooltip>
        ) : (
          STORE_RELATIONS_FALLBACK_TEXT
        )}
      </td>

      <td className="oc-td-text store-relations-client-cell">
        {rowState.storeRef !== STORE_RELATIONS_FALLBACK_TEXT ? (
          <Tooltip
            title={rowState.storeRef}
            arrow
            placement="top"
            enterDelay={300}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8]
                    }
                  }
                ]
              }
            }}
          >
            <Box component="span" className="store-relations-client-text">
              {rowState.storeRef}
            </Box>
          </Tooltip>
        ) : (
          STORE_RELATIONS_FALLBACK_TEXT
        )}
      </td>

      <td className="oc-td-text store-relations-client-cell">
        {rowState.clientLabel !== STORE_RELATIONS_FALLBACK_TEXT ? (
          <Tooltip
            title={rowState.clientLabel.toUpperCase()}
            arrow
            placement="top"
            enterDelay={300}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8]
                    }
                  }
                ]
              }
            }}
          >
            <Box component="span" className="store-relations-client-text">
              {rowState.clientLabel.toUpperCase()}
            </Box>
          </Tooltip>
        ) : (
          STORE_RELATIONS_FALLBACK_TEXT
        )}
      </td>

      <td className="oc-td-text">
        {rowState.principalRelations.length ? (
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            {rowState.principalRelations.map((relation, index) => (
              <StoreRelationUserChip
                key={getStoreRelationUserChipKey({
                  relation,
                  index,
                  suffix: 'principal',
                  status: rowState.hasPrincipalConflict ? 'error' : relation?.status || 'active'
                })}
                variant="principal"
                status={rowState.hasPrincipalConflict ? 'error' : relation?.status || 'active'}
                label={getStoreRelationUserChipLabel(relation)}
              />
            ))}
          </Box>
        ) : (
          <StoreRelationUserChip
            variant="principal"
            status="unassigned"
            label={STORE_RELATIONS_FALLBACK_TEXT}
          />
        )}
      </td>

      <td className="oc-td-text store-relations-secondary-cell">
        {!rowState.secondaryUsers.length ? (
          STORE_RELATIONS_FALLBACK_TEXT
        ) : (
          <Box display="flex" flexDirection="column" gap={0.75}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {rowState.initialSecondaryUsers.map((relation, index) => (
                <StoreRelationUserChip
                  key={getStoreRelationUserChipKey({
                    relation,
                    index,
                    suffix: 'initial',
                    status: getStoreRelationUserChipStatus(relation)
                  })}
                  status={getStoreRelationUserChipStatus(relation)}
                  label={getStoreRelationUserChipLabel(relation)}
                />
              ))}
            </Box>

            {rowState.hasHiddenSecondaryUsers && (
              <>
                <Collapse in={showAllSecondaryUsers} timeout={300}>
                  <Box
                    className="store-relations-secondary-extra"
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                  >
                    {rowState.extraSecondaryUsers.map((relation, index) => (
                      <StoreRelationUserChip
                        key={getStoreRelationUserChipKey({
                          relation,
                          index,
                          suffix: 'extra',
                          status: getStoreRelationUserChipStatus(relation)
                        })}
                        status={getStoreRelationUserChipStatus(relation)}
                        label={getStoreRelationUserChipLabel(relation)}
                      />
                    ))}
                  </Box>
                </Collapse>

                <Link
                  component="button"
                  type="button"
                  className="store-relations-secondary-toggle"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowAllSecondaryUsers((prev) => !prev)
                  }}
                >
                  {showAllSecondaryUsers
                    ? STORE_RELATIONS_SECONDARY_USERS_TOGGLE_LABELS.COLLAPSE
                    : STORE_RELATIONS_SECONDARY_USERS_TOGGLE_LABELS.EXPAND}
                </Link>
              </>
            )}
          </Box>
        )}
      </td>

      <td className="oc-td-text store-relations-status-cell">
        <div className="td-delivery-status store-relations-status-wrap">
          <div className={`delivery-status ${rowState.statusUi?.className || ''}`}>
            {rowState.statusUi?.label || rowState.overallStatus}
          </div>
        </div>
      </td>

      <td
        className="oc-action-cell action-wrapper store-relations-action-cell"
        onClick={(e) => e.stopPropagation()}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <DropdownActionButtonMui
            id={`btn-dropdown-${rowState.rowId || 'store-relation'}`}
            list={actions}
            disabled={!canWrite || !actions.length}
          />
        </Box>
      </td>
    </tr>
  )
}

export default StoreRelationsRow
